import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { pb, isUserValid } from "../services/pocketbase";
import { Link } from "react-router-dom";

const BlackOps6Leaderboard = () => {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoggedIn = isUserValid();
  const [selectedMap, setSelectedMap] = useState("all");
  const [filteredClips, setFilteredClips] = useState([]);

  // Maps data
  const maps = [
    { value: "all", label: "All Maps" },
    { value: "liberty_falls", label: "Liberty Falls" },
    { value: "terminus", label: "Terminus" },
    { value: "cittadelle_de_morts", label: "Cittadelle de Morts" },
    { value: "the_toumb", label: "The Toumb" },
  ];

  // Function to fetch Black Ops 6 clips sorted by highest round
  const fetchTopRoundClips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!isLoggedIn) {
        setLoading(false);
        return;
      }

      // Get all Black Ops 6 clips
      const clipRecords = await pb.collection("clips").getFullList({
        filter: 'game="blackops6"',
        expand: "user",
      });

      // Sort by highest round
      const sortedClips = clipRecords.sort((a, b) => {
        // Convert to number to ensure proper sorting
        const roundA = parseInt(a.highest_round) || 0;
        const roundB = parseInt(b.highest_round) || 0;
        return roundB - roundA;
      });

      // For any clips without user data, fetch it separately
      const clipsWithUserData = await Promise.all(
        sortedClips.map(async (clip) => {
          if (clip.expand?.user) {
            return {
              ...clip,
              userData: clip.expand.user,
            };
          }

          try {
            const userData = await pb.collection("users").getOne(clip.user);
            return {
              ...clip,
              userData,
            };
          } catch (err) {
            console.error("Error fetching user data:", err);
            return clip;
          }
        })
      );

      setClips(clipsWithUserData);
    } catch (err) {
      console.error("Error fetching top round clips:", err);
      setError("Failed to load leaderboard. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  // Filter clips when selectedMap changes
  useEffect(() => {
    if (clips.length > 0) {
      if (selectedMap === "all") {
        setFilteredClips(clips);
      } else {
        const filtered = clips.filter((clip) => clip.map === selectedMap);
        setFilteredClips(filtered);
      }
    } else {
      setFilteredClips([]);
    }
  }, [clips, selectedMap]);

  useEffect(() => {
    fetchTopRoundClips();
  }, [fetchTopRoundClips]);

  // Function to handle map filter change
  const handleMapChange = (event) => {
    setSelectedMap(event.target.value);
  };

  // Function to get YouTube video ID from link
  const getVideoId = (youtubeLink) => {
    if (!youtubeLink) return null;

    try {
      // Handle different YouTube URL formats
      let videoId = null;

      if (youtubeLink.includes("youtu.be/")) {
        videoId = youtubeLink.split("youtu.be/")[1].split("?")[0];
      } else if (youtubeLink.includes("youtube.com/watch")) {
        const urlParams = new URLSearchParams(youtubeLink.split("?")[1]);
        videoId = urlParams.get("v");
      } else if (youtubeLink.includes("youtube.com/embed/")) {
        videoId = youtubeLink.split("youtube.com/embed/")[1].split("?")[0];
      }

      return videoId;
    } catch (err) {
      console.error("Error parsing YouTube link:", err);
      return null;
    }
  };

  // Function to get map display name
  const getMapName = (mapId) => {
    const maps = {
      liberty_falls: "Liberty Falls",
      terminus: "Terminus",
      cittadelle_de_morts: "Cittadelle de Morts",
      the_toumb: "The Toumb",
    };
    return maps[mapId] || mapId;
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading leaderboard...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!isLoggedIn) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            borderRadius: 2,
          }}
        >
          <Alert
            severity="info"
            sx={{
              mb: 3,
              width: "80%",
              maxWidth: "500px",
              "& .MuiAlert-message": { fontSize: "1.1rem" },
            }}
          >
            Please login to view leaderboard
          </Alert>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{ mb: 4, textAlign: "center" }}
      >
        BLACK OPS 6 LEADERBOARD
      </Typography>

      {/* Map Filter */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="map-filter-label">Filter by Map</InputLabel>
          <Select
            labelId="map-filter-label"
            id="map-filter"
            value={selectedMap}
            label="Filter by Map"
            onChange={handleMapChange}
          >
            {maps.map((map) => (
              <MenuItem key={map.value} value={map.value}>
                {map.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Top 3 Clips */}
      <Box sx={{ mb: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {/* Second place (left) */}
          {(filteredClips[1] || filteredClips.length === 0) && (
            <Grid
              item
              xs={12}
              md={4}
              key={filteredClips[1]?.id || "second-placeholder"}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: "2px solid silver",
                  bgcolor: "rgba(40, 44, 52, 0.99)",
                  minWidth: "280px",
                }}
              >
                {/* Rank Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "silver",
                    color: "black",
                    px: 1.5,
                    py: 0.5,
                    fontWeight: "bold",
                    zIndex: 1,
                  }}
                >
                  #2
                </Box>

                {/* Map Badge */}
                {filteredClips[1] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bgcolor: "rgba(40, 44, 52, 0.8)",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      zIndex: 1,
                      maxWidth: "70%", // Prevent overlap with rank number
                    }}
                  >
                    <Chip
                      label={getMapName(filteredClips[1]?.map)}
                      size="small"
                      sx={{
                        bgcolor: "#c4d4e0",
                        color: "black",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>
                )}

                {/* Video Embed or Placeholder */}
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "56.25%",
                    overflow: "hidden",
                  }}
                >
                  {filteredClips[1] &&
                  getVideoId(filteredClips[1]?.clip_link) ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getVideoId(
                        filteredClips[1]?.clip_link
                      )}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      title={filteredClips[1]?.clip_title}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <Typography>No video available</Typography>
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom noWrap>
                    {filteredClips[1]?.clip_title || "Waiting for submission"}
                  </Typography>

                  {filteredClips[1] ? (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        src={
                          filteredClips[1]?.userData?.avatar
                            ? `${pb.baseUrl}/api/files/users/${filteredClips[1]?.userData?.id}/${filteredClips[1]?.userData?.avatar}`
                            : null
                        }
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {filteredClips[1]?.userData?.username || "Unknown User"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Be the second to submit a clip!
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Highest Round{" "}
                      <Typography component="span" fontWeight="bold">
                        {filteredClips[1]?.highest_round || "-"}
                      </Typography>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* First place (center) */}
          {(filteredClips[0] || filteredClips.length === 0) && (
            <Grid
              item
              xs={12}
              md={4}
              key={filteredClips[0]?.id || "first-placeholder"}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: "2px solid gold",
                  bgcolor: "rgba(40, 44, 52, 0.99)",
                  transform: { xs: "scale(1)", md: "scale(1.05)" },
                  zIndex: 2,
                  minWidth: "280px", // Added minimum width to prevent clipping
                }}
              >
                {/* Rank Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "gold",
                    color: "black",
                    px: 1.5,
                    py: 0.5,
                    fontWeight: "bold",
                    zIndex: 1,
                  }}
                >
                  #1
                </Box>

                {/* Map Badge */}
                {filteredClips[0] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bgcolor: "rgba(40, 44, 52, 0.8)",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      zIndex: 1,
                      maxWidth: "70%", // Prevent overlap with rank number
                    }}
                  >
                    <Chip
                      label={getMapName(filteredClips[0]?.map)}
                      size="small"
                      sx={{
                        bgcolor: "#c4d4e0",
                        color: "black",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>
                )}

                {/* Video Embed or Placeholder */}
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "56.25%",
                    overflow: "hidden",
                  }}
                >
                  {filteredClips[0] &&
                  getVideoId(filteredClips[0]?.clip_link) ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getVideoId(
                        filteredClips[0]?.clip_link
                      )}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      title={filteredClips[0]?.clip_title}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <Typography>No video available</Typography>
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom noWrap>
                    {filteredClips[0]?.clip_title || "Waiting for submission"}
                  </Typography>

                  {filteredClips[0] ? (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        src={
                          filteredClips[0]?.userData?.avatar
                            ? `${pb.baseUrl}/api/files/users/${filteredClips[0]?.userData?.id}/${filteredClips[0]?.userData?.avatar}`
                            : null
                        }
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {filteredClips[0]?.userData?.username || "Unknown User"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Be the first to submit a clip!
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Highest Round{" "}
                      <Typography component="span" fontWeight="bold">
                        {filteredClips[0]?.highest_round || "-"}
                      </Typography>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Third place (right) */}
          {(filteredClips[2] || filteredClips.length === 0) && (
            <Grid
              item
              xs={12}
              md={4}
              key={filteredClips[2]?.id || "third-placeholder"}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: "2px solid #cd7f32", // bronze
                  bgcolor: "rgba(40, 44, 52, 0.99)",
                  minWidth: "280px", // Added minimum width to prevent clipping
                }}
              >
                {/* Rank Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "#cd7f32", // Bronze color
                    color: "black",
                    px: 1.5,
                    py: 0.5,
                    fontWeight: "bold",
                    zIndex: 1,
                  }}
                >
                  #3
                </Box>

                {/* Map Badge */}
                {filteredClips[2] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bgcolor: "rgba(40, 44, 52, 0.8)",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      zIndex: 1,
                      maxWidth: "70%", // Prevent overlap with rank number
                    }}
                  >
                    <Chip
                      label={getMapName(filteredClips[2]?.map)}
                      size="small"
                      sx={{
                        bgcolor: "#c4d4e0",
                        color: "black",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>
                )}

                {/* Video Embed or Placeholder */}
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "56.25%",
                    overflow: "hidden",
                  }}
                >
                  {filteredClips[2] &&
                  getVideoId(filteredClips[2]?.clip_link) ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getVideoId(
                        filteredClips[2]?.clip_link
                      )}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      title={filteredClips[2]?.clip_title}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <Typography>No video available</Typography>
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom noWrap>
                    {filteredClips[2]?.clip_title || "Waiting for submission"}
                  </Typography>

                  {filteredClips[2] ? (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        src={
                          filteredClips[2]?.userData?.avatar
                            ? `${pb.baseUrl}/api/files/users/${filteredClips[2]?.userData?.id}/${filteredClips[2]?.userData?.avatar}`
                            : null
                        }
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {filteredClips[2]?.userData?.username || "Unknown User"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Be the third to submit a clip!
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Highest Round{" "}
                      <Typography component="span" fontWeight="bold">
                        {filteredClips[2]?.highest_round || "-"}
                      </Typography>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Rest of the leaderboard (4th place and beyond) */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Other Top Clips
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ bgcolor: "rgba(40, 44, 52, 0.99)" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Player</TableCell>
                <TableCell>Map</TableCell>
                <TableCell>Highest Round</TableCell>
                <TableCell>Clip</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClips.length > 0 ? (
                filteredClips.slice(3).map((clip, index) => (
                  <TableRow key={clip.id}>
                    <TableCell>#{index + 4}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar
                          src={
                            clip.userData?.avatar
                              ? `${pb.baseUrl}/api/files/users/${clip.userData.id}/${clip.userData.avatar}`
                              : null
                          }
                          sx={{ width: 24, height: 24, mr: 1 }}
                        />
                        {clip.userData?.username || "Unknown User"}
                      </Box>
                    </TableCell>
                    <TableCell>{getMapName(clip.map)}</TableCell>
                    <TableCell align="right">{clip.highest_round}</TableCell>
                    <TableCell>
                      {clip.clip_link ? (
                        <Button
                          size="small"
                          variant="outlined"
                          href={clip.clip_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Watch
                        </Button>
                      ) : (
                        "No clip"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No additional entries yet. Be the first to submit your high
                    round!
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default BlackOps6Leaderboard;
